import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Upload, Send, FileText, Download, Sparkles, BookOpen, Brain } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import GanttChart from './GanttChart';
import { TextWithCharts } from './ChartRenderer';
import Footer from './Footer';
import AdvisorRibbon from './AdvisorRibbon';
import SEOHead from './SEOHead';

const API = process.env.REACT_APP_BACKEND_URL;

const LearnResearch = () => {
  const { user, openAuth } = useContext(AuthContext);
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'url'
  const [selectedFile, setSelectedFile] = useState(null);
  const [paperUrl, setPaperUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(30); // Countdown timer in seconds
  const [paper, setPaper] = useState(null);
  const [question, setQuestion] = useState('');
  const [qaHistory, setQaHistory] = useState([]);
  const [showProposal, setShowProposal] = useState(false);
  const [timeline, setTimeline] = useState('');
  const [showGantt, setShowGantt] = useState(false);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      toast.success(`Selected: ${file.name}`);
    } else {
      toast.error('Please select a PDF file');
    }
  };

  const uploadPaper = async () => {
    // Check if user is authenticated first
    if (!user) {
      openAuth('register');
      return;
    }
    
    if (uploadMethod === 'file' && !selectedFile) {
      toast.error('Please select a PDF file first');
      return;
    }
    
    if (uploadMethod === 'url' && !paperUrl.trim()) {
      toast.error('Please enter a paper URL');
      return;
    }

    setLoading(true);
    setCountdown(30); // Start countdown from 30 seconds
    
    // Start countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    try {
      const token = localStorage.getItem('token');
      
      let response;
      if (uploadMethod === 'file') {
        const formData = new FormData();
        formData.append('file', selectedFile);

        response = await axios.post(
          `${API}/api/research/upload`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // URL upload
        response = await axios.post(
          `${API}/api/research/upload-url`,
          { url: paperUrl },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      clearInterval(countdownInterval);
      setPaper(response.data);
      toast.success('✨ Paper analyzed! Ready to help you understand it.');
      setSelectedFile(null);
      setPaperUrl('');
    } catch (error) {
      clearInterval(countdownInterval);
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload paper');
    } finally {
      clearInterval(countdownInterval);
      setLoading(false);
      setCountdown(30);
    }
  };

  const askQuestion = async () => {
    if (!question.trim() || !paper) return;
    
    // Check if user is authenticated
    if (!user) {
      openAuth('register');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/research/${paper.paper_id}/ask`,
        null,
        {
          params: { question },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setQaHistory([...qaHistory, {
        question: response.data.question,
        answer: response.data.answer
      }]);
      setQuestion('');
      toast.success('Answer ready!');
    } catch (error) {
      console.error('Question failed:', error);
      toast.error('Failed to answer question');
    } finally {
      setLoading(false);
    }
  };

  const extractProposal = async () => {
    if (!paper) return;
    
    // Check if user is authenticated
    if (!user) {
      openAuth('register');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/research/${paper.paper_id}/extract-proposal`,
        null,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setPaper({ ...paper, proposal: response.data.proposal });
      setShowProposal(true);
      toast.success('📝 Research proposal extracted!');
    } catch (error) {
      console.error('Proposal extraction failed:', error);
      toast.error('Failed to extract proposal');
    } finally {
      setLoading(false);
    }
  };

  const generateGantt = async () => {
    if (!timeline.trim() || !paper || !paper.proposal) return;
    
    // Check if user is authenticated
    if (!user) {
      openAuth('register');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/research/${paper.paper_id}/generate-gantt`,
        null,
        {
          params: { timeline },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setPaper({
        ...paper,
        gantt_chart: response.data.gantt_chart,
        timeline: response.data.timeline
      });
      setShowGantt(true);
      toast.success('📊 Timeline created!');
    } catch (error) {
      console.error('Gantt generation failed:', error);
      toast.error('Failed to generate timeline');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async () => {
    if (!paper) {
      toast.error('No paper loaded');
      return;
    }
    
    // Check if user is authenticated
    if (!user) {
      openAuth('register');
      return;
    }

    const toastId = toast.loading('Preparing document...');
    
    try {
      const token = localStorage.getItem('token');
      
      console.log('Starting download for paper ID:', paper.paper_id);
      console.log('API URL:', `${API}/api/research/${paper.paper_id}/download`);
      
      // Use fetch instead of axios for better blob handling
      const response = await fetch(
        `${API}/api/research/${paper.paper_id}/download`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      console.log('Blob received:', blob.size, 'bytes, type:', blob.type);

      // Dismiss loading toast
      toast.dismiss(toastId);

      // Verify we got data
      if (!blob || blob.size === 0) {
        throw new Error('No data received from server');
      }

      // Create download URL
      const url = window.URL.createObjectURL(blob);
      const fileName = `Research_Proposal_${paper.title.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}.docx`;
      
      console.log('Creating download with filename:', fileName);
      
      // Try using the download attribute approach
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      
      // Add to DOM, click, then remove
      document.body.appendChild(link);
      
      // Force click
      link.click();
      
      console.log('Download triggered');
      
      // Cleanup
      setTimeout(() => {
        try {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          console.log('Cleanup completed');
        } catch (e) {
          console.error('Cleanup error:', e);
        }
      }, 100);
      
      // Show success message
      setTimeout(() => {
        toast.success('📥 Document downloaded! Check your Downloads folder.');
      }, 500);
      
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Download failed:', error);
      toast.error('Failed to download document. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold gradient-text-gold mb-2 font-playfair">
            Learn Research
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-white font-outfit">
            India's First AI that Helps you Understand Published Research !
          </p>
          <div className="mt-4 max-w-3xl mx-auto bg-[#0a0e17] border-l-4 border-gold-primary p-4 rounded-r-lg">
            <p className="text-xs sm:text-sm text-white font-outfit">
              <strong className="gradient-text-gold">Important Note:</strong> Extracting Research Proposal Features is only available to understand the process of Research Proposal writing to Indian Students who are not aware of designing a Research Study.
            </p>
          </div>
        </div>

        {/* How to Use - Flowchart */}
        <Card className="mb-6 bg-[#0a0e17] border border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 gradient-text-gold text-base sm:text-lg font-outfit">
              <Sparkles className="h-4 w-4 text-gold-primary" />
              How to Use Learn Research
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {/* Step 1 */}
              <div className="flex items-start gap-2 bg-white rounded-md p-3 shadow-sm border-l-4 border-blue-500">
                <div className="bg-blue-700 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Upload className="h-4 w-4 text-blue-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">Upload Your Paper</h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    Upload PDF or paste URL from arXiv, PubMed, etc.
                  </p>
                </div>
              </div>

              {/* Arrow Down */}
              <div className="flex justify-center">
                <div className="text-blue-500 text-lg">↓</div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-2 bg-white rounded-md p-3 shadow-sm border-l-4 border-purple-500">
                <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Brain className="h-4 w-4 text-purple-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">Nerd-AI Simplifies</h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    Nerd AI explains the paper in simplified form
                  </p>
                </div>
              </div>

              {/* Arrow Down */}
              <div className="flex justify-center">
                <div className="text-purple-500 text-lg">↓</div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-2 bg-white rounded-md p-3 shadow-sm border-l-4 border-green-500">
                <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <FileText className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-gray-900 text-sm">Ask & Extract</h3>
                  </div>
                  <p className="text-xs text-gray-600">
                    Ask questions, clear doubts & extract proposal
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3 p-2 bg-white rounded-md border border-blue-200">
              <p className="text-xs text-gray-600 text-center">
                <strong>💡 Tip:</strong> Use arXiv, PubMed, bioRxiv. Avoid paywalled content.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Upload Section */}
        {!paper && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Your Research Paper
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Upload Method Selector */}
                <div className="flex gap-2 border-b pb-4">
                  <Button
                    variant={uploadMethod === 'file' ? 'default' : 'outline'}
                    onClick={() => setUploadMethod('file')}
                    className="flex-1"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload PDF
                  </Button>
                  <Button
                    variant={uploadMethod === 'url' ? 'default' : 'outline'}
                    onClick={() => setUploadMethod('url')}
                    className="flex-1"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Paste URL
                  </Button>
                </div>

                {/* File Upload */}
                {uploadMethod === 'file' && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="pdf-upload"
                    />
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-700 mb-2">
                        {selectedFile ? selectedFile.name : 'Click to upload PDF'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Upload a research paper and I'll help you understand it!
                      </p>
                    </label>
                  </div>
                )}

                {/* URL Upload */}
                {uploadMethod === 'url' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Research Paper URL
                    </label>
                    <input
                      type="url"
                      value={paperUrl}
                      onChange={(e) => setPaperUrl(e.target.value)}
                      placeholder="https://arxiv.org/abs/2301.12345 or any research paper URL"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-900 font-medium mb-1">
                        📄 Best Sources (Free Access):
                      </p>
                      <ul className="text-xs text-blue-800 space-y-1 ml-4 list-disc">
                        <li><strong>arXiv</strong> - Free preprints (arxiv.org)</li>
                        <li><strong>PubMed Central</strong> - Free full-text articles</li>
                        <li><strong>bioRxiv / medRxiv</strong> - Biology & medicine preprints</li>
                        <li><strong>Direct PDF links</strong> from any source</li>
                      </ul>
                      <p className="text-xs text-orange-700 mt-2 font-medium">
                        ⚠️ Note: Commercial publishers (Nature, ScienceDirect, IEEE) often require subscriptions. Use arXiv or open-access versions when available.
                      </p>
                    </div>
                  </div>
                )}
                
                <Button
                  onClick={uploadPaper}
                  disabled={(uploadMethod === 'file' && !selectedFile) || (uploadMethod === 'url' && !paperUrl.trim()) || loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Analyzing... ({countdown}s remaining)
                    </span>
                  ) : (
                    'Analyze Paper'
                  )}
                </Button>
                {loading && (
                  <div className="mt-3 text-center">
                    <p className="text-sm text-gray-600">
                      🔍 Extracting and analyzing content... Please wait
                    </p>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${((30 - countdown) / 30) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Paper Explanation */}
        {paper && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    {paper.title}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPaper(null);
                      setQaHistory([]);
                      setShowProposal(false);
                      setShowGantt(false);
                    }}
                  >
                    New Paper
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                    <p className="text-sm text-blue-700 font-medium">
                      📚 Your Research Mentor's Explanation:
                    </p>
                  </div>
                  <div className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg prose-strong:font-bold prose-strong:text-gray-900">
                    {/* Render any embedded charts */}
                    <TextWithCharts text={paper.explanation} />
                    
                    {/* Render the markdown text */}
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        br: () => <br />,
                        p: ({node, ...props}) => <p className="mb-3 text-gray-700" {...props} />,
                        h1: ({node, ...props}) => <h1 className="font-bold text-3xl text-gray-900 mb-4 mt-6" {...props} />,
                        h2: ({node, ...props}) => <h2 className="font-bold text-2xl text-gray-900 mb-3 mt-5" {...props} />,
                        h3: ({node, ...props}) => <h3 className="font-bold text-xl text-gray-900 mb-2 mt-4" {...props} />,
                        h4: ({node, ...props}) => <h4 className="font-bold text-lg text-gray-900 mb-2 mt-3" {...props} />,
                        strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />
                      }}
                    >
                      {paper.explanation.replace(/```chart-json[\s\S]*?```/g, '')}
                    </ReactMarkdown>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Q&A Section */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Ask Questions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Q&A History */}
                  {qaHistory.map((qa, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <p className="font-medium text-gray-900">You: {qa.question}</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg">
                        {/* Render any embedded charts */}
                        <TextWithCharts text={qa.answer} />
                        
                        {/* Render the markdown text */}
                        <div className="prose prose-base max-w-none prose-headings:font-bold prose-strong:font-bold">
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            components={{
                              br: () => <br />,
                              p: ({node, ...props}) => <p className="mb-2 text-gray-700" {...props} />,
                              h1: ({node, ...props}) => <h1 className="font-bold text-2xl text-gray-900 mb-3 mt-4" {...props} />,
                              h2: ({node, ...props}) => <h2 className="font-bold text-xl text-gray-900 mb-2 mt-3" {...props} />,
                              h3: ({node, ...props}) => <h3 className="font-bold text-lg text-gray-900 mb-2 mt-3" {...props} />,
                              h4: ({node, ...props}) => <h4 className="font-bold text-base text-gray-900 mb-1 mt-2" {...props} />,
                              strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />
                            }}
                          >
                            {qa.answer.replace(/```chart-json[\s\S]*?```/g, '')}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Question Input */}
                  <div className="flex gap-2">
                    <Textarea
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      placeholder="Ask me anything about this paper..."
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && askQuestion()}
                    />
                    <Button
                      onClick={askQuestion}
                      disabled={!question.trim() || loading}
                      className="bg-blue-500 hover:bg-blue-700"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Extract Proposal Button - After Q&A */}
            {!showProposal && (
              <div className="mb-6">
                <Button
                  onClick={extractProposal}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-lg py-8 shadow-xl"
                >
                  <Sparkles className="h-6 w-6 mr-2" />
                  Create a Research Proposal Template !
                </Button>
                <p className="text-center text-sm text-gray-500 mt-2">
                  Click to reverse-engineer the research proposal from this paper
                </p>
                <div className="mt-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>Important Note:</strong> This tool is for young aspiring scientists to gain understanding on the structure of the research proposal. This tool doesn't create Research proposals for anyone's research.
                  </p>
                </div>
              </div>
            )}

            {/* Research Proposal */}
            {showProposal && paper.proposal && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Research Proposal</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg prose-strong:font-bold">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        br: () => <br />,
                        p: ({node, ...props}) => <p className="mb-3 text-gray-700" {...props} />,
                        h1: ({node, ...props}) => <h1 className="font-bold text-3xl text-gray-900 mb-4 mt-6" {...props} />,
                        h2: ({node, ...props}) => <h2 className="font-bold text-2xl text-gray-900 mb-3 mt-5" {...props} />,
                        h3: ({node, ...props}) => <h3 className="font-bold text-xl text-gray-900 mb-2 mt-4" {...props} />,
                        h4: ({node, ...props}) => <h4 className="font-bold text-lg text-gray-900 mb-2 mt-3" {...props} />,
                        strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />
                      }}
                    >
                      {paper.proposal}
                    </ReactMarkdown>
                  </div>

                  {/* Timeline Question */}
                  {!showGantt && (
                    <div className="mt-6 space-y-4">
                      <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                        <p className="text-purple-800 font-medium mb-2">
                          What is the timeline request for this research?
                        </p>
                        <p className="text-sm text-purple-600">
                          Examples: "6 months", "1 year", "2 years", "3 years"
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={timeline}
                          onChange={(e) => setTimeline(e.target.value)}
                          placeholder="e.g., 1 year"
                          className="flex-1 px-4 py-2 border rounded-lg"
                        />
                        <Button
                          onClick={generateGantt}
                          disabled={!timeline.trim() || loading}
                          className="bg-purple-500 hover:bg-purple-600"
                        >
                          Create Timeline
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Gantt Chart */}
            {showGantt && paper.gantt_chart && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Research Timeline ({paper.timeline})</CardTitle>
                </CardHeader>
                <CardContent>
                  <GanttChart ganttText={paper.gantt_chart} timeline={paper.timeline} />
                </CardContent>
              </Card>
            )}

            {/* Download Button */}
            {showProposal && (
              <div className="space-y-2">
                <Button
                  onClick={downloadDocument}
                  className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-lg py-6"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download as Word Document
                </Button>
                <p className="text-center text-xs text-gray-500">
                  💡 If download doesn't start, check your browser's download settings
                </p>
              </div>
            )}
          </>
        )}
        
        {/* Bottom spacing */}
        <div className="pb-24"></div>
      </div>
      
      <AdvisorRibbon />
      <Footer />
    </div>
  );
};

export default LearnResearch;
